// Pure derivation of Training Plan state from assessment history + the three
// user-record timestamps. No store of its own (mirrors utils/nudgeCalculation.js):
// every input is already reactive MST, so observer components recompute on render.
//
// Core mechanic: bucket the user's practices by LOCAL calendar day, then replay
// them in date order against PLAN_DAYS — a day that satisfies the current plan
// day's required practice slots advances the counter by one. A calendar day can
// complete at most one plan day (you can't binge the plan; extra sessions are
// "bonus"). Review slots complete on click (user.trainingPlanChecks) and never
// gate advancement, so pre-feature history replays correctly.

import {
  SLOTS,
  PLAN_DAYS,
  PLAN_LENGTH_DAYS,
  PLAN_EXPIRY_DAYS,
  ENTRY_THRESHOLD,
  RESURFACE_AFTER_DAYS,
  GRADUATION_LINGER_DAYS,
} from './trainingPlan';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// L2 interactive recordings are stored as `..._2.mp4` — the same heuristic
// DashboardPage uses; sessionType is identical across levels. Only used for
// default-CTA copy and celebration, never as a completion gate (days 6-10
// accept either variant).
export const isInteractiveAssessment = (a) => /_2\.mp4$/.test(a?.videoName || '');

// LOCAL calendar day, deliberately unlike the UTC weekly buckets in
// streakCalculation.js: a 5pm PST practice must not count as tomorrow.
export const localDayKey = (dateInput) => {
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const getPracticeAssessments = (assessmentsStore, userId, companyId) => {
  if (!assessmentsStore?.closedAssessmentsForUser || !userId || !companyId) return [];
  // closedAssessmentsForUser filters salesperson.userId AND companyId; live-call
  // reports share the list but are not practices.
  return assessmentsStore
    .closedAssessmentsForUser(userId, companyId)
    .filter((a) => a.sessionType !== 'Live Meeting' && a.completedDateTime);
};

export const getCompanyReadiness = (company) => {
  const bg = company?.background;
  const hasProfile = !!(
    (bg && (((bg.history || '').trim()) || ((bg.mission || '').trim()))) ||
    (company?.differentiators?.length) ||
    (company?.addressableProblems?.length) ||
    (company?.offerings?.length) ||
    (company?.prospectProfile?.length)
  );
  const hasQaLibrary = !!(company?.questionsAndAnswers?.length);
  return { hasProfile, hasQaLibrary };
};

const requirementMet = (requires, readiness) => {
  if (!requires) return true;
  if (requires === 'companyProfile') return readiness.hasProfile;
  if (requires === 'qaLibrary') return readiness.hasQaLibrary;
  return true;
};

// Resolve one plan day against current company readiness. Unavailable slots
// render locked-with-reason and are excluded from the day's requirements.
export const resolvePlanDay = (dayIndex, readiness) => {
  const idx = Math.max(0, Math.min(dayIndex, PLAN_DAYS.length - 1));
  return PLAN_DAYS[idx]
    .map((slotId) => SLOTS[slotId])
    .filter(Boolean)
    .map((slot) => {
      const availableLaunchers = (slot.launchers || []).filter((l) => requirementMet(l.requires, readiness));
      const available = slot.kind === 'review'
        ? true
        : requirementMet(slot.requires, readiness) && availableLaunchers.length > 0;
      return { slot, available, availableLaunchers };
    });
};

const assessmentMatchesSlot = (slot, assessment) =>
  (slot.completes || []).some((m) => assessment.sessionType === m.sessionType);

// Required = available practice slots. Review slots never gate advancement.
const requiredSlots = (resolvedDay) =>
  resolvedDay.filter((r) => r.available && r.slot.kind === 'practice');

const daySatisfies = (resolvedDay, dayAssessments) => {
  const required = requiredSlots(resolvedDay);
  if (!required.length) return false;
  return required.every((r) => dayAssessments.some((a) => assessmentMatchesSlot(r.slot, a)));
};

export const computePlanState = ({ currentUser, company, assessmentsStore, now = new Date() }) => {
  const readiness = getCompanyReadiness(company);
  const userId = currentUser?.userId;
  const companyId = company?.id;

  // Manager assignment ("redo training"): trainingPlanAssignedAt ANCHORS a
  // fresh run. Everything older than the anchor — practices, review checks,
  // welcome/dismiss/complete stamps — is invisible to this run, so a veteran
  // genuinely restarts at Day 1. Epoch comparisons (not string) so mixed
  // timestamp formats can't mis-bucket.
  // ACCEPTED LIMITATION: the anchor is account-global (one field on the user),
  // while plan progress is otherwise company-scoped. A multi-company assignee
  // therefore sees the assigned run in every company context, not just the one
  // the manager assigned from. Consciously accepted (multi-company users are
  // rare); revisit with a trainingPlanAssignedCompanyId gate if it matters.
  const assignedAtRaw = currentUser?.trainingPlanAssignedAt || null;
  const anchorMs = assignedAtRaw ? new Date(assignedAtRaw).getTime() : NaN;
  const assigned = !!assignedAtRaw && !isNaN(anchorMs);
  const afterAnchor = (iso) => {
    if (!assigned) return true;
    const t = new Date(iso).getTime();
    return !isNaN(t) && t >= anchorMs;
  };
  // A stored stamp only counts for the CURRENT run.
  const effective = (iso) => (iso && afterAnchor(iso) ? iso : null);

  const allPractices = getPracticeAssessments(assessmentsStore, userId, companyId);
  const practices = assigned
    ? allPractices.filter((a) => afterAnchor(a.completedDateTime))
    : allPractices;
  // Run-local count: for assigned runs this counts only post-anchor practice
  // (entry gating is bypassed for assigned runs anyway).
  const practiceCount = practices.length;

  // Group by local day, then replay oldest-first. History replays against
  // CURRENT company readiness — a close-enough approximation that keeps this
  // derivation stateless.
  const byDay = new Map();
  practices.forEach((a) => {
    const key = localDayKey(a.completedDateTime);
    if (!key) return;
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(a);
  });

  const todayKey = localDayKey(now);
  const pastDayKeys = [...byDay.keys()].filter((k) => k < todayKey).sort();

  let completedBeforeToday = 0;
  for (const key of pastDayKeys) {
    if (completedBeforeToday >= PLAN_LENGTH_DAYS) break;
    const resolvedDay = resolvePlanDay(completedBeforeToday, readiness);
    if (daySatisfies(resolvedDay, byDay.get(key))) completedBeforeToday++;
  }

  const currentDayIndex = Math.min(completedBeforeToday, PLAN_LENGTH_DAYS - 1);
  const currentDayNumber = currentDayIndex + 1;
  const todaysPractices = byDay.get(todayKey) || [];
  const checks = currentUser?.trainingPlanChecks || {};

  const resolvedToday = resolvePlanDay(currentDayIndex, readiness);
  let nextAssigned = false;
  const todaySlots = resolvedToday.map(({ slot, available, availableLaunchers }) => {
    const done = slot.kind === 'review'
      ? !!(checks[slot.id] && afterAnchor(checks[slot.id]))
      : todaysPractices.some((a) => assessmentMatchesSlot(slot, a));
    const doneInteractive = slot.kind === 'practice' && done &&
      todaysPractices.some((a) => assessmentMatchesSlot(slot, a) && isInteractiveAssessment(a));
    const isNext = !nextAssigned && available && !done;
    if (isNext) nextAssigned = true;
    return {
      slot,
      available,
      availableLaunchers,
      required: available && slot.kind === 'practice',
      done,
      doneInteractive,
      isNext,
      lockedReason: available ? null : slot.unavailableReason || null,
    };
  });

  const required = todaySlots.filter((r) => r.required);
  const todayRequiredCount = required.length;
  const todayDoneCount = required.filter((r) => r.done).length;
  const todayComplete = completedBeforeToday >= PLAN_LENGTH_DAYS ||
    (todayRequiredCount > 0 && todayDoneCount === todayRequiredCount);
  const bonusToday = Math.max(0, todaysPractices.length - todayDoneCount);

  const completedDays = Math.min(
    PLAN_LENGTH_DAYS,
    completedBeforeToday + (todayComplete && completedBeforeToday < PLAN_LENGTH_DAYS ? 1 : 0)
  );
  const effectiveCompletedAt = effective(currentUser?.trainingPlanCompletedAt);
  const planComplete = completedDays >= PLAN_LENGTH_DAYS || !!effectiveCompletedAt;
  // The dashboard stamps completion when the derived run finishes; keyed on the
  // EFFECTIVE stamp so an assigned re-run (old stamp pre-anchor) re-stamps.
  const needsCompletionStamp = completedDays >= PLAN_LENGTH_DAYS && !effectiveCompletedAt;

  const nextSlot = todaySlots.find((r) => r.isNext) || null;
  const nextPractice = todaySlots.find((r) => r.required && !r.done) || null;

  // Tomorrow's practice set, for the day-complete banner ("Come back tomorrow
  // for Day N: ..."). Only meaningful once today is done and days remain.
  const tomorrowTitles = (todayComplete && completedDays < PLAN_LENGTH_DAYS)
    ? resolvePlanDay(completedDays, readiness)
      .filter((r) => r.available && r.slot.kind === 'practice')
      .map((r) => r.slot.title)
    : [];

  // --- Visibility ---
  // Only Multiply platform staff are excluded. Read-only Execs/Managers DO
  // practice — the backend write-block exempts recordings/uploads, murphy
  // session endpoints, and self-profile PATCHes, so the whole plan flow
  // (sessions, progress, review checks) works for them.
  const roleEligible = !!userId && currentUser?.userType !== 'admin';
  const welcomeSeenAt = effective(currentUser?.trainingPlanWelcomeSeenAt);
  const dismissedAt = effective(currentUser?.trainingPlanDismissedAt);

  const daysSince = (iso) => {
    if (!iso) return Infinity;
    const t = new Date(iso).getTime();
    if (isNaN(t)) return Infinity;
    return (now.getTime() - t) / MS_PER_DAY;
  };

  // Assigned runs can expire even before the user first sees the welcome
  // (anchor fallback); organic runs still only expire after the welcome.
  const expiryAnchor = welcomeSeenAt || (assigned ? assignedAtRaw : null);
  const expired = !!expiryAnchor && daysSince(expiryAnchor) > PLAN_EXPIRY_DAYS && !planComplete;

  // The graduation panel lingers briefly, then auto-retires so a graduate who
  // never clicks Finish doesn't suppress DashboardNudges forever. Anchored on
  // the completion stamp (written the first time the completed panel renders),
  // so the derived-complete-but-never-stamped case still shows once.
  const graduationRetired = planComplete &&
    !!effectiveCompletedAt &&
    daysSince(effectiveCompletedAt) > GRADUATION_LINGER_DAYS;

  // practices is sorted newest-first (closedAssessments ordering).
  const lastPracticeAt = practices[0]?.completedDateTime || null;
  const dormantDays = Math.min(daysSince(dismissedAt), daysSince(lastPracticeAt));
  const resurfaced = !!dismissedAt && practiceCount < ENTRY_THRESHOLD && dormantDays > RESURFACE_AFTER_DAYS;

  const welcomeEligible = roleEligible &&
    (practiceCount < ENTRY_THRESHOLD || assigned) &&
    !welcomeSeenAt &&
    !dismissedAt &&
    !planComplete &&
    !expired;

  // Entry gate (< ENTRY_THRESHOLD practices), already started (welcome seen),
  // or manager-assigned: mid-plan users keep the panel past 3 practices until
  // done/expired/dismissed.
  const panelVisible = roleEligible &&
    !expired &&
    !graduationRetired &&
    (practiceCount < ENTRY_THRESHOLD || !!welcomeSeenAt || assigned) &&
    (!dismissedAt || resurfaced);

  return {
    roleEligible,
    practiceCount,
    assigned,
    assignedAt: assigned ? assignedAtRaw : null,
    effectiveWelcomeSeenAt: welcomeSeenAt,
    effectiveDismissedAt: dismissedAt,
    effectiveCompletedAt,
    needsCompletionStamp,
    welcomeEligible,
    panelVisible,
    resurfaced,
    expired,
    planComplete,
    completedBeforeToday,
    completedDays,
    currentDayNumber,
    todaySlots,
    todayRequiredCount,
    todayDoneCount,
    todayComplete,
    bonusToday,
    nextSlot,
    nextPractice,
    tomorrowTitles,
    totalDays: PLAN_LENGTH_DAYS,
  };
};

// Compact per-member plan status for manager surfaces (Team Dashboard).
// Returns null when the plan isn't applicable to the user (Multiply staff,
// read-only observers, veterans who never entered), so callers can simply
// hide the badge. Pure and safe to call for ANY user object.
export const getTrainingPlanStatusForUser = ({ user, company, assessmentsStore, now = new Date() }) => {
  try {
    const s = computePlanState({ currentUser: user, company, assessmentsStore, now });
    if (!s.roleEligible) return null;
    // "Entered the plan" = there is a real signal the user was ever in it
    // (welcome shown, assigned, completion stamped, or dismissed). planComplete
    // alone is NOT enough: a veteran whose organic history merely HAPPENS to
    // satisfy the day pattern would otherwise read as "Plan complete" despite
    // never seeing the plan. Such users fall through to null (no badge).
    const entered = !!(s.effectiveWelcomeSeenAt || s.assigned || s.effectiveCompletedAt || s.effectiveDismissedAt);
    if (s.planComplete && entered) return { kind: 'complete', label: 'Plan complete' };
    if (s.effectiveDismissedAt && !s.resurfaced) return { kind: 'dismissed', label: 'Plan hidden' };
    if (s.expired) return { kind: 'lapsed', label: 'Plan lapsed' };
    if (s.effectiveWelcomeSeenAt || s.assigned) return { kind: 'active', label: `Day ${s.currentDayNumber} of ${s.totalDays}` };
    if (s.practiceCount < ENTRY_THRESHOLD && !s.planComplete) return { kind: 'notStarted', label: 'Not started' };
    return null;
  } catch {
    return null;
  }
};
